// src/lib/resources/relevance/generator-scoring.ts

/**
 * Pure relevance-scoring functions for the generator detail page's
 * "Related X" cluster. Used by:
 *
 *   - `~/lib/resources/api/related-resources.ts`
 *     (server-side, precomputed and passed via prop on first paint)
 *   - `~/components/resources/layouts/GeneratorLayout.tsx`
 *     (client-side fallback path)
 *
 * Lives in a `'use server'`/`'use client'`-free module so both
 * surfaces can import directly — no Server-Reference wrapping, no
 * client-bundle pollution.
 *
 * Generator scoring differs from calculator scoring in two ways:
 *   1. Honors an explicit `relatedTools` list on the generator
 *      resource (+50 score) — generators can hand-curate cross-links
 *      via a YAML field; calculators currently don't.
 *   2. Splits keyword matches into "direct" vs "partial" buckets with
 *      separate weights, vs the calculator's single-bucket sum.
 */

import type { ResourceTypeMap } from '~/lib/resources/types/shared/base'
import {
  ToolCategories,
  GlossaryCategories,
} from '~/lib/resources/types/shared/enums'
import type {
  ToolCategory,
  GlossaryCategory,
} from '~/lib/resources/types/shared/enums'
import { scoreSharedTopicHubAffinity, scoreGlossaryTopicHubAffinity } from '~/lib/resources/relevance/topic-hub-affinity'

type Generator = Extract<ResourceTypeMap['tool'], { toolType: 'generator' }>
type GlossaryTerm = ResourceTypeMap['glossary-term']

/**
 * Score how related a sibling tool is to the current generator.
 *
 * Weighting:
 *   - Explicit `relatedTools` entry (by id or slug):  +50
 *   - Category match (resource category):              +5
 *   - Tool-category match:                             +5
 *   - Description/keyword superset (either direction): +10
 *   - Per direct-keyword match:                        +3
 *   - Per partial (substring) keyword match:           +2
 */
export function scoreToolForGenerator(
  tool: ResourceTypeMap['tool'],
  currentTool: Generator,
): number {
  let score = 0

  if (
    currentTool.relatedTools &&
    Array.isArray(currentTool.relatedTools) &&
    (currentTool.relatedTools.includes(tool.id) ||
      currentTool.relatedTools.includes(tool.slug))
  ) {
    score += 50
  }

  if (tool.category === currentTool.category) score += 5
  if (tool.toolCategory === currentTool.toolCategory) score += 5

  const toolContent = [
    tool.title,
    tool.description,
    tool.metadata?.keywords?.join(' ') ?? '',
    tool.category ?? '',
    tool.toolCategory ?? '',
  ]
    .join(' ')
    .toLowerCase()

  const currentContent = [
    currentTool.title,
    currentTool.description,
    currentTool.metadata?.keywords?.join(' ') ?? '',
    currentTool.category ?? '',
    currentTool.toolCategory ?? '',
  ]
    .join(' ')
    .toLowerCase()

  if (toolContent.includes(currentContent) || currentContent.includes(toolContent)) {
    score += 10
  }

  const toolKeywords = tool.metadata?.keywords ?? tool.tags ?? []
  const currentKeywords = currentTool.metadata?.keywords ?? currentTool.tags ?? []

  const directMatches = toolKeywords.filter((k) => currentKeywords.includes(k)).length
  score += directMatches * 3

  const partialMatches =
    toolKeywords.filter((k) =>
      currentKeywords.some(
        (ck) =>
          k.toLowerCase().includes(ck.toLowerCase()) ||
          ck.toLowerCase().includes(k.toLowerCase()),
      ),
    ).length - directMatches
  score += partialMatches * 2

  score += scoreSharedTopicHubAffinity(currentTool.slug, tool.slug)

  return score
}

/**
 * Score how related a glossary term is to the current generator.
 *
 * Generators benefit more from glossary cross-links than calculators
 * (which already surface metric definitions in their FAQ), so the
 * scorer fires across many dimensions:
 *   - Generator keyword matches term title/acronym exactly: +30
 *   - Term title/acronym appears in generator title:        +25
 *   - Term title/acronym appears anywhere in generator copy: +10
 *   - Tool-category-themed keyword in term title:           +10
 *   - Glossary-category × tool-category affinity match:     +5
 *   - Generator slug contains 'persona' and term is
 *     persona/audience/target/segment-themed:               +20
 *   - Per related-term echo in generator copy:              +3
 *
 * The category-keyword rules and the glossary×tool affinity rules are
 * data tables (declared inline below) so the weighting reads as
 * rules rather than nested conditionals.
 */
export function scoreGlossaryForGenerator(
  term: GlossaryTerm,
  generator: Generator,
): number {
  let score = 0

  const termTitle = term.title.toLowerCase()
  const termAcronym = term.acronym?.toLowerCase() ?? ''
  const generatorKeywords = generator.metadata?.keywords ?? generator.tags ?? []

  if (
    generatorKeywords.some(
      (keyword) =>
        keyword.toLowerCase() === termTitle || keyword.toLowerCase() === termAcronym,
    )
  ) {
    score += 30
  }

  const generatorContent = [
    generator.title,
    generator.description,
    generator.metadata?.keywords?.join(' ') ?? '',
    generator.category ?? '',
    generator.toolCategory ?? '',
    generator.slug.split('-').join(' '),
  ]
    .join(' ')
    .toLowerCase()

  if (
    generator.title.toLowerCase().includes(termTitle) ||
    (termAcronym && generator.title.toLowerCase().includes(termAcronym))
  ) {
    score += 25
  }

  if (
    generatorContent.includes(termTitle) ||
    (termAcronym && generatorContent.includes(termAcronym))
  ) {
    score += 10
  }

  const categoryKeywordRules: Array<{
    category: ToolCategory
    keywords: string[]
  }> = [
    {
      category: ToolCategories.AUDIENCE,
      keywords: ['audience', 'targeting', 'persona', 'segment', 'demographic', 'psychographic'],
    },
    {
      category: ToolCategories.ANALYTICS,
      keywords: ['analytics', 'insights', 'metrics', 'performance', 'data', 'reporting', 'analysis'],
    },
    {
      category: ToolCategories.CREATIVE,
      keywords: ['creative', 'design', 'video', 'ad'],
    },
    {
      category: ToolCategories.PERFORMANCE,
      keywords: ['performance', 'metrics', 'kpis', 'key performance indicators'],
    },
    {
      category: ToolCategories.DATA_ANALYSIS,
      keywords: ['data', 'analysis', 'reporting', 'insights'],
    },
    {
      category: ToolCategories.PLATFORM,
      keywords: [
        'platform', 'channel', 'network', 'ad platform',
        'facebook', 'instagram', 'tiktok', 'youtube',
        'linkedin', 'twitter', 'pinterest', 'social media',
      ],
    },
    {
      category: ToolCategories.STRATEGY,
      keywords: ['strategy', 'planning', 'tactics', 'goals'],
    },
  ]

  for (const rule of categoryKeywordRules) {
    if (generator.toolCategory === rule.category) {
      if (rule.keywords.some((k) => termTitle.includes(k))) {
        score += 10
      }
    }
  }

  const glossaryCategoryRules: Array<{
    glossaryCategory: GlossaryCategory
    toolCategory: ToolCategory
    fallbackCategory?: string
  }> = [
    { glossaryCategory: GlossaryCategories.PLATFORM, toolCategory: ToolCategories.PLATFORM, fallbackCategory: 'platform' },
    { glossaryCategory: GlossaryCategories.CREATIVE, toolCategory: ToolCategories.CREATIVE, fallbackCategory: 'creative' },
    { glossaryCategory: GlossaryCategories.METRICS, toolCategory: ToolCategories.ANALYTICS, fallbackCategory: 'analytics' },
    { glossaryCategory: GlossaryCategories.METRICS, toolCategory: ToolCategories.PERFORMANCE, fallbackCategory: 'performance' },
    { glossaryCategory: GlossaryCategories.CHARTS, toolCategory: ToolCategories.DATA_ANALYSIS, fallbackCategory: 'data-analysis' },
    { glossaryCategory: GlossaryCategories.GENERAL, toolCategory: ToolCategories.STRATEGY, fallbackCategory: 'strategy' },
  ]

  for (const rule of glossaryCategoryRules) {
    if (
      term.category === rule.glossaryCategory &&
      (generator.toolCategory === rule.toolCategory ||
        generator.category === rule.fallbackCategory)
    ) {
      score += 5
    }
  }

  if (
    generator.slug.includes('persona') &&
    ['persona', 'audience', 'target', 'segment'].some((k) => termTitle.includes(k))
  ) {
    score += 20
  }

  if (term.relatedTerms?.length) {
    const relatedMatches = term.relatedTerms.filter((rt) =>
      generatorContent.includes(rt.title.toLowerCase()),
    ).length
    score += relatedMatches * 3
  }

  score += scoreGlossaryTopicHubAffinity(term.category, term.slug, generator.slug)

  return score
}
